import { animate, state, style, transition, trigger } from '@angular/animations';
import { AsyncPipe, DatePipe, DecimalPipe } from '@angular/common';
import {
  AfterContentInit,
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { BehaviorSubject, combineLatest, fromEvent, Observable, Subscription } from 'rxjs';
import { debounceTime, map, skip } from 'rxjs/operators';
import { PRODUCT_PLACEHOLDER } from 'src/app/app.settings';
import {
  Allergens,
  AllergensNames,
  discountLevels,
  InnerLineMap,
  LineMap,
  OnlineStripContent,
  Product,
  ProductComposition,
  ProductDetails,
  ProductLine,
  ProductModule,
  Saving,
  StoreType,
} from 'src/app/models';
import { PickerType, SelectCount, SharedDetailContent } from 'src/app/models/product-detail';
import { buttonIcons, symbolIcons } from 'src/app/modules/icon.module';
import { BasketService } from 'src/app/services/basket/basket.service';
import { ContentService } from 'src/app/services/content/content.service';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { KioskService } from 'src/app/services/kiosk/kiosk.service';
import { findPackageCombination } from 'src/app/services/product/product-package-combination';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { CenterSectionComponent, IconButtonComponent, NutritionalContentComponent, TextButtonComponent } from 'src/app/shared/components';
import { ImageCarouselComponent } from 'src/app/shared/components/image-carousel';
import { SavingsComponent } from 'src/app/shared/components/product/card/savings/savings.component';
import { CounterComponent } from 'src/app/shared/components/product/counter/counter.component';
import { PriceFieldComponent } from 'src/app/shared/components/product/price-field/price-field.component';
import { ContentSection } from 'src/app/shared/layout/content-section';
import { StripContainerComponent } from 'src/app/shared/layout/strips/strip-container.component';
import { environment } from 'src/environments/environment';
import { ProductDeliveryLimitPipe } from '../../../../../shared/pipes/delivery-limit.pipe';
import { DisplayCurrencyPipe } from '../../../../../shared/pipes/display-currency.pipe';
import { PdfNamePipe } from '../../../../../shared/pipes/pdf-name.pipe';
import { PriceSumPipe } from '../../../../../shared/pipes/price-sum.pipe';
import { SanitizeHtmlPipe } from '../../../../../shared/pipes/sanitize-html.pipe';
import { ProductAdditionsComponent } from '../../product-additions/product-additions.component';
import { ProductMainOptionsComponent } from './main-options/main-options.component';
import { MenyBasketDialogComponent } from './meny-basket/meny-basket-dialog.component';
import { ProductPickerComponent } from './product-picker/product-picker.component';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-composite-product-detail',
  templateUrl: './composite-product-detail.component.html',
  styleUrls: ['./composite-product-detail.component.scss'],
  animations: [
    trigger('slide', [
      transition(':enter', [style({ opacity: 0, 'margin-right': '50px' }), animate('400ms', style({ opacity: 1, 'margin-right': '*' }))]),
      transition(':leave', [style({ opacity: 1 }), animate('200ms', style({ opacity: 0 }))]),
    ]),
    trigger('openClose', [
      state(
        'open',
        style({
          transform: 'translateY(0)',
          opacity: 1,
        }),
      ),
      state(
        'closed',
        style({
          transform: 'translateY(100%)',
          opacity: 0,
        }),
      ),
      transition('open => closed', [animate('500ms ease-out')]),
      transition('closed => open', [animate('500ms ease-out')]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CenterSectionComponent,
    SavingsComponent,
    ImageCarouselComponent,
    SanitizeHtmlPipe,
    PriceFieldComponent,
    ProductMainOptionsComponent,
    AsyncPipe,
    TextButtonComponent,
    MatIconModule,
    ProductDeliveryLimitPipe,
    DatePipe,
    NutritionalContentComponent,
    PdfNamePipe,
    ProductPickerComponent,
    ProductAdditionsComponent,
    DecimalPipe,
    DisplayCurrencyPipe,
    CounterComponent,
    PriceSumPipe,
    IconButtonComponent,
    StripContainerComponent,
    MatExpansionModule,
  ],
})
export class CompositeProductDetailComponent extends ContentSection implements OnChanges, AfterContentInit, AfterViewChecked, OnDestroy {
  private dialogService = inject(DialogService);
  private basketService = inject(BasketService);
  private themeService = inject(ThemeService);
  private kioskService = inject(KioskService);
  private elRef = inject(ElementRef);
  private snackBar = inject(MatSnackBar);

  contactUsUrl = `/${environment.urls.content.contact_us}`;

  private subscription: Subscription;
  private stickySubscription: Subscription;
  private viewChanged: boolean;

  basketOverviewUrl = `/${environment.urls.basket}`;
  deleteIcon = buttonIcons.bigCross;
  arrowIcon = symbolIcons.left_arrow;
  locale = environment.i18n.language;
  currency = environment.i18n.currency;

  @Input() product!: ProductDetails;
  composition: ProductComposition;
  compositePrice: number;
  discountLevelPercent: number;
  validated: boolean;
  showAltPrice: boolean;
  requiredModules: Record<number, boolean>;
  validateTimeout: any;

  @Input() sharedContent: SharedDetailContent;
  @Input() basketProducts: Observable<any>;
  @Input() price: number;
  @Input() savings: Saving;
  @Output() reload = new EventEmitter<boolean>();

  @ViewChild('composite', { read: ElementRef, static: false }) compositeRef!: ElementRef;
  @ViewChild('stickyBottom', { read: ElementRef, static: false }) stickyRef!: ElementRef;
  @ViewChild('description', { read: ElementRef, static: false }) descriptionRef!: ElementRef;

  @HostBinding('class.kiosk') isKiosk: boolean;

  basketProductLine: Observable<ProductLine>;
  lineId: BehaviorSubject<number>;
  placeholder = PRODUCT_PLACEHOLDER;

  combineMenuButtonLabel: string;

  mainOptions: boolean;
  mainOnlyOptions: boolean;
  sticky: boolean;
  totalOffset: number;
  calculateOffsetRef: any;
  toggleStickyRef: any;

  isFandanko: boolean;
  isDagrofa: boolean;
  isMeny: boolean;
  showCounter: boolean;
  quantityCounter: number;
  summaryLabel: string;
  preparationGuideLabel: string;
  mobileScrollDownShow: boolean;
  showTopAddToBasket: boolean;

  strips: OnlineStripContent[];

  constructor() {
    const contentService = inject(ContentService);
    super(contentService, 'productInfo');

    this.subscription = new Subscription();

    const { type, showCompositeTopAddToCart } = this.contentService.getMerchant();
    this.isFandanko = type === StoreType.FANDANKO || type === StoreType.FCKING_PIZZA;
    this.isMeny = type === StoreType.MENY;
    this.isDagrofa = this.contentService.isDagrofa;
    this.isKiosk = this.kioskService.isKiosk;
    this.showTopAddToBasket = showCompositeTopAddToCart;

    this.combineMenuButtonLabel = this.content.get('combineMenuButtonLabelText') || 'Sammensæt din ret'; // TODO: make text feature please
    this.preparationGuideLabel = this.content.get('preparationGuideText') || 'Tilberedningsvejledning';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['product']) {
      this.showCounter = true;

      this.mainOnlyOptions = true;
      for (const module of this.product.productModules) {
        module.products.sort((a, b) => a.priority - b.priority);
        if (module.main) {
          this.mainOptions = true;
        }

        if (!module.main) this.mainOnlyOptions = false;

        if (!module.singles) {
          this.showCounter = false;
        }
      }

      this.product.descriptionExtensions.sort((a, b) => a.index - b.index);
      this.strips = this.contentService.getOnlineProductStripContent(this.product.id);

      this.quantityCounter = this.product.minCount || 1;

      if (this.product.packageOptions?.length) {
        const packageSizes = this.product.packageOptions.map((option) => option.packageSize);
        const [sizeOne, sizeTwo] = findPackageCombination(this.quantityCounter, packageSizes);
        if (sizeOne < 0) this.quantityCounter += sizeTwo;
      }

      this.composition = this.initComposition();
      this.validated = this.validate();
      this.lineId = new BehaviorSubject<number>(undefined);
      this.requiredModules = {};

      this.summaryLabel = this.product.summary;
      if (this.isDagrofa) {
        const minPickCount = this.getSelectCount().minPickCount;
        if (this.quantityCounter > 1 || minPickCount > 1)
          this.summaryLabel = `Minimum ${this.quantityCounter > 1 ? this.quantityCounter : minPickCount} kuverter`;
      }

      this.basketProductLine = combineLatest([this.basketProducts, this.lineId.pipe(skip(1))]).pipe(
        map(([products, lineId]: [LineMap, number]) => {
          const { id } = this.composition;

          this.composition.lineId = lineId;
          const product: ProductLine = (products?.[id] as InnerLineMap)?.[lineId];

          return product;
        }),
      );
      this.viewChanged = true;
    }
  }

  ngAfterContentInit() {
    this.themeService.subscribeComponent(this.elRef);
  }

  ngAfterViewChecked() {
    if (typeof document === 'undefined' || !this.viewChanged) return;
    this.viewChanged = false;

    // Sticky total
    this.stickySubscription?.unsubscribe();
    this.stickySubscription = new Subscription();

    this.calculateOffsetRef = this.calculateOffset.bind(this);
    this.toggleStickyRef = this.toggleSticky.bind(this);
    this.stickySubscription.add(fromEvent(window, 'resize').pipe(debounceTime(1)).subscribe(this.calculateOffsetRef));
    this.stickySubscription.add(fromEvent(window, 'scroll').pipe().subscribe(this.toggleStickyRef));

    setTimeout(() => {
      this.calculateOffset();
    }, 0);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.stickySubscription?.unsubscribe();
  }

  initComposition(): ProductComposition {
    const composition = {
      ...this.product,
      moduleChoices: {},
    };

    this.compositePrice = this.price;
    this.discountLevelPercent = 0;
    this.showAltPrice = true;

    return composition;
  }

  add(productData: [ProductModule, Product, PickerType, number]) {
    new Promise<boolean>((resolve) => {
      this.showAltPrice = false;

      const module = productData[0];
      const product = productData[1];
      const type = productData[2];
      const quantity = productData[3] || 1;

      // does module choice exist?
      if (!this.composition.moduleChoices[module.id]) {
        this.composition.moduleChoices[module.id] = { name: module.name, products: {} };
      }

      const moduleChoices = this.composition.moduleChoices[module.id];

      if (type === 'pick-one') {
        for (const p of Object.values(moduleChoices.products)) {
          this.compositePrice -= p.price * p.quantity;
        }
        moduleChoices.products = {};
      }

      if (type === 'pick-many' && module.maxPickCount && !module.main) {
        const choicesCount = Object.keys(moduleChoices.products).length;
        if (choicesCount >= module.maxPickCount) {
          this.requiredModules[module.id] = true;
          this.snackBar.open(`Du må højst vælge ${module.maxPickCount} stk. for ${module.name}`, 'luk', {
            duration: 3000,
            panelClass: 'warn',
          });
          if (this.validateTimeout) clearTimeout(this.validateTimeout);
          this.validateTimeout = setTimeout(() => this.validateModuleQuantity(module), 3000);
          return resolve(true);
        }
      }

      // is this the first time the product is chosen?
      if (!moduleChoices.products[product.id]) {
        moduleChoices.products[product.id] = { ...product, quantity };
        this.compositePrice += product.price * quantity;
        return resolve(false);
      }

      if (type === 'pick-many' || type === 'pick-one') {
        return resolve(false);
      }

      const updatedQuantity = moduleChoices.products[product.id].quantity + quantity;
      moduleChoices.products[product.id] = { ...product, quantity: updatedQuantity };
      this.compositePrice += product.price * quantity;

      return resolve(false);
    }).then((skipModuleValidate: boolean) => {
      if (this.composition.discountLevels) {
        let moduleQuantity = 0;
        Object.values(this.composition.moduleChoices).forEach((moduleChoice) => {
          Object.values(moduleChoice.products).forEach((product) => {
            moduleQuantity += product.quantity;
          });
        });

        this.discountLevelPercent = 0;
        this.composition.discountLevels.forEach((discountLevel: discountLevels) => {
          if (
            discountLevel.includeModuleProducts &&
            discountLevel.minimumQuantity <= moduleQuantity &&
            this.discountLevelPercent < discountLevel.discountPercent
          ) {
            this.discountLevelPercent = discountLevel.discountPercent;
          }
        });
      }

      this.validated = this.validate();
      if (!skipModuleValidate) this.validateModuleQuantity(productData[0]);
    });
  }

  async remove(productData: [ProductModule, Product, PickerType, number]) {
    new Promise<void>((resolve) => {
      const module = productData[0];
      const product = productData[1];
      const type = productData[2];
      const quantity = productData[3] || 1;
      const moduleChoices = this.composition.moduleChoices[module.id];

      // does module choice and product exist?
      if (!(moduleChoices && moduleChoices.products[product.id])) {
        return resolve();
      }

      // is there only one of the product?
      if (moduleChoices.products[product.id].quantity === quantity) {
        delete moduleChoices.products[product.id];
        this.compositePrice -= product.price * quantity;
        this.showAltPrice = Object.values(this.composition.moduleChoices).find((mc) => Object.keys(mc.products).length > 0) === undefined;
        return resolve();
      }

      const updatedQuantity = moduleChoices.products[product.id].quantity - quantity;
      moduleChoices.products[product.id] = { ...product, quantity: updatedQuantity };
      this.compositePrice -= product.price * quantity;
      return resolve();
    }).finally(() => {
      if (this.composition.discountLevels) {
        let moduleQuantity = 0;
        Object.values(this.composition.moduleChoices).forEach((moduleChoice) => {
          Object.values(moduleChoice.products).forEach((product) => {
            moduleQuantity += product.quantity;
          });
        });

        this.discountLevelPercent = 0;
        this.composition.discountLevels.forEach((discountLevel: discountLevels) => {
          if (
            discountLevel.includeModuleProducts &&
            discountLevel.minimumQuantity <= moduleQuantity &&
            this.discountLevelPercent < discountLevel.discountPercent
          ) {
            this.discountLevelPercent = discountLevel.discountPercent;
          }
        });
      }

      this.validated = this.validate();
      this.validateModuleQuantity(productData[0]);
    });
  }

  private validate(): boolean {
    let totalQuantity = 0;
    for (const module of this.product.productModules) {
      const { minPickCount, maxPickCount, singles } = module;
      const moduleChoices = this.composition.moduleChoices[module.id];

      if (!moduleChoices && minPickCount > 0) {
        return false;
      }

      if (moduleChoices) {
        let quantity = 0;
        for (const product of Object.values(moduleChoices.products)) {
          quantity += product.quantity;

          const { minCount, productMaxPickCount } = module.products.find((p) => p.id === product.id);
          if (!singles && ((productMaxPickCount && product.quantity > productMaxPickCount) || product.quantity < minCount)) {
            return false;
          }
        }

        if ((maxPickCount && quantity > maxPickCount) || quantity < minPickCount) {
          return false;
        }

        totalQuantity += quantity;
      }
    }

    if (this.product.price === 0 && totalQuantity === 0) {
      return false;
    }

    return true;
  }

  private validateModuleQuantity(module: ProductModule) {
    if (this.validateTimeout) clearTimeout(this.validateTimeout);
    let quantity = 0;
    const moduleChoices = this.composition.moduleChoices[module.id];
    for (const product of Object.values(moduleChoices.products)) {
      quantity += product.quantity;
    }

    if (!this.requiredModules[module.id]) {
      if (module.maxPickCount && module.maxPickCount < quantity) {
        this.requiredModules[module.id] = true;
      }
      return;
    }

    if (module.minPickCount <= quantity && (!module.maxPickCount || quantity <= module.maxPickCount)) {
      delete this.requiredModules[module.id];
    }
  }

  validateAndAddToBasket(mainElement: HTMLElement, pickerElement: HTMLElement) {
    if (!this.validated) {
      return this.validateRequiredAndScroll(mainElement, pickerElement);
    }

    this.addToBasket(this.composition.minCount);
  }

  addToBasket(quantity: number) {
    quantity = quantity || 1;

    if (this.validated) {
      const line = this.basketService.addProduct(this.composition, quantity);
      this.lineId.next(line.lineId);
    }
  }

  removeFromBasket(prevQuantity: number, quantity: number) {
    quantity = quantity || 1;

    if (this.validated) {
      if (prevQuantity === quantity) {
        this.deleteFromBasket();
        return;
      }

      const line = this.basketService.removeProduct(this.composition, quantity);
      this.lineId.next(line?.lineId);
    }
  }

  deleteFromBasket() {
    this.basketService.deleteProduct(this.composition);
    this.buyMore();
  }

  validateRequiredAndScroll(mainElement: HTMLElement, pickerElement: HTMLElement) {
    const requiredModules: Record<number, boolean> = {};
    let mainRequired = false;

    for (const module of this.product.productModules) {
      const { minPickCount, maxPickCount, id, main } = module;
      if (minPickCount <= 0) {
        continue;
      }

      const moduleChoices = this.composition.moduleChoices[id];

      if (!moduleChoices) {
        requiredModules[id] = true;
        if (main) mainRequired = true;
        continue;
      }

      let quantity = 0;
      for (const product of Object.values(moduleChoices.products)) {
        quantity += product.quantity;

        const { minCount, productMaxPickCount } = module.products.find((p) => p.id === product.id);
        if ((productMaxPickCount && product.quantity > productMaxPickCount) || product.quantity < minCount) {
          requiredModules[id] = true;
          if (main) mainRequired = true;
        }
      }

      if (quantity < minPickCount || (maxPickCount && maxPickCount < quantity)) {
        requiredModules[id] = true;
        if (main) mainRequired = true;
      }
    }
    this.requiredModules = requiredModules;

    if (mainRequired) {
      window.scroll({ top: mainElement.getBoundingClientRect().top + window.scrollY, behavior: 'smooth' });
    } else {
      this.scroll(pickerElement);
    }
  }

  scroll(el: HTMLElement) {
    el.scrollIntoView({ behavior: 'smooth' });
  }

  calculateOffset() {
    const { offsetHeight } = this.compositeRef.nativeElement;
    const { clientHeight } = this.stickyRef.nativeElement;
    const top = this.compositeRef.nativeElement.getBoundingClientRect().top + window.scrollY;
    this.totalOffset = offsetHeight + top + (this.sticky ? 0 : clientHeight);

    this.compositeRef.nativeElement.style.setProperty('--sticky-height', `${clientHeight}px`);
    this.toggleSticky();
  }

  toggleSticky() {
    this.sticky = window.scrollY + window.innerHeight <= this.totalOffset || this.isKiosk;
    this.mobileScrollDownShow = this.isElementBelowViewport(this.descriptionRef.nativeElement, this.stickyRef.nativeElement.clientHeight);
  }

  isElementBelowViewport(el: HTMLElement, offset?: number) {
    const rect = el.getBoundingClientRect();
    return rect.top + (offset || 0) > window.innerHeight;
  }

  buyMore() {
    this.composition = this.resetComposition();
    this.validated = this.validate();
    this.lineId.next(undefined);
  }

  resetComposition(): ProductComposition {
    const composition = {
      ...this.product,
      moduleChoices: {},
    };

    const { maxPickCount } = this.composition.productModules[0];

    if (!maxPickCount || maxPickCount !== 1) {
      this.showAltPrice = true;
      this.compositePrice = this.price;
      this.discountLevelPercent = 0;
      return composition;
    }

    composition.moduleChoices = this.composition.moduleChoices;

    return composition;
  }

  getSelectCount(): SelectCount {
    const selectCount: SelectCount = {
      minPickCount: 0,
      pickedCount: 0,
    };

    this.product.productModules.forEach((module) => {
      if (module.maxPickCount !== 1 && !module.singles && module.minPickCount > 0) {
        selectCount.minPickCount += module.minPickCount;
        if (this.composition.moduleChoices[module.id]?.products) {
          Object.values(this.composition.moduleChoices[module.id].products).forEach((product) => {
            selectCount.pickedCount += product.quantity;
          });
        }
      }
    });

    return selectCount;
  }

  openBasket() {
    this.addToBasket(this.quantityCounter);
    this.dialogService.openDialog(MenyBasketDialogComponent, 'Kurv', {
      data: {
        product: this.composition,
        lineId: this.lineId.value,
        initialQuantity: this.quantityCounter,
      },
      classes: ['meny-basket-dialog'],
    });
    this.buyMore();
  }

  addToQuantityCounter(quantity: number) {
    this.quantityCounter += quantity || 1;
  }

  removeFromQuantityCounter(quantity: number) {
    this.quantityCounter -= quantity || 1;
    this.quantityCounter = this.quantityCounter < this.composition.minCount ? this.composition.minCount : this.quantityCounter;
  }

  getAllergensString(allergens: Allergens[]): string {
    const allergensNames = allergens.map((allergen) => AllergensNames[allergen]);
    return allergensNames.join(', ');
  }

  openLinkDialog(label: string, url: string) {
    this.dialogService.openLinkDialog(label, { url });
  }
}
